import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { niciApi } from '../../services/api'
import { NiciOfferta, StatoOffertaNici } from '../../types/nici'
import { Calculator } from 'lucide-react'

const STATI: { value: StatoOffertaNici | ''; label: string }[] = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'BOZZA', label: 'Bozza' },
  { value: 'DA_APPROVARE', label: 'Da approvare' },
  { value: 'APPROVATA', label: 'Approvata' },
  { value: 'INVIATA', label: 'Inviata' },
  { value: 'ACCETTATA', label: 'Accettata' },
  { value: 'RIFIUTATA', label: 'Rifiutata' },
  { value: 'SUPERATA', label: 'Superata' },
]

const COLORI_STATO: Record<StatoOffertaNici, { bg: string; color: string }> = {
  BOZZA:         { bg: 'rgba(100,116,139,.12)', color: '#64748b' },
  DA_APPROVARE:  { bg: 'rgba(245,158,11,.12)',  color: '#d97706' },
  APPROVATA:     { bg: 'rgba(59,130,246,.12)',  color: '#2563eb' },
  INVIATA:       { bg: 'rgba(124,58,237,.12)',  color: '#7c3aed' },
  ACCETTATA:     { bg: 'rgba(34,197,94,.12)',   color: '#15803d' },
  RIFIUTATA:     { bg: 'rgba(220,38,38,.12)',   color: '#dc2626' },
  SUPERATA:      { bg: 'rgba(100,116,139,.08)', color: '#94a3b8' },
}

function BadgeStato({ stato }: { stato: StatoOffertaNici }) {
  const s = COLORI_STATO[stato]
  return (
    <span className="badge text-xs px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>
      {stato.replace('_', ' ')}
    </span>
  )
}

export default function NiciOfferte() {
  const [stato, setStato] = useState<StatoOffertaNici | ''>('')

  const { data, isLoading } = useQuery({
    queryKey: ['nici-offerte', stato],
    queryFn: () => niciApi.offerte.list(stato ? { stato } : undefined).then(r => r.data as { items: NiciOfferta[] }),
  })
  const offerte = data?.items ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Offerte / Computi</h1>
        <p className="page-subtitle">Offerte generate dalle pratiche di sopralluogo NICI</p>
      </div>

      <select className="select w-56" value={stato} onChange={e => setStato(e.target.value as StatoOffertaNici | '')}>
        {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>

      {isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
      ) : offerte.length === 0 ? (
        <div className="card p-12 text-center space-y-2">
          <Calculator size={40} className="mx-auto" style={{ color: '#cbd5e1' }} />
          <p className="text-slate-500 font-medium">Nessuna offerta trovata</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="p-3">Codice</th><th className="p-3">Rev.</th><th className="p-3">Stato</th>
                <th className="p-3 text-right">Totale ricavo</th>
                {offerte[0]?.margine_pct !== undefined && <th className="p-3 text-right">Margine %</th>}
              </tr>
            </thead>
            <tbody>
              {offerte.map(o => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="p-3">
                    <Link to={`/nici/offerte/${o.id}`} className="font-medium" style={{ color: '#1E40AF' }}>{o.codice}</Link>
                  </td>
                  <td className="p-3 text-xs text-slate-500">v{o.revisione}{o.corrente ? '' : ' (superata)'}</td>
                  <td className="p-3"><BadgeStato stato={o.stato} /></td>
                  <td className="p-3 text-right">€ {o.totale_ricavo.toFixed(2)}</td>
                  {o.margine_pct !== undefined && (
                    <td className="p-3 text-right" style={{ color: (o.margine_pct ?? 0) < 20 ? '#dc2626' : '#15803d' }}>
                      {(o.margine_pct ?? 0).toFixed(1)}%
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
